import mixpanel, { Mixpanel } from 'mixpanel-browser';

import {
  addSignOutListener,
  EmployeeProfile,
  UserProfileResponse,
} from '@sbv-clients/data-access-api';

import { environment } from '../environments/environment';

let instance: Mixpanel | undefined;

const setup = (): Mixpanel | undefined => {
  const { mixpanelToken } = environment;
  if (!mixpanelToken) {
    return undefined;
  }

  mixpanel.init(mixpanelToken, {
    debug: process.env.NODE_ENV === 'development',
    ip: false,
    persistence: 'localStorage',
  });
  mixpanel.register({
    platform: 'web',
    environment: environment.environment,
  });

  return mixpanel;
};

const withMixpanel = (use: (instance: Mixpanel) => void): void => {
  try {
    instance ??= setup();
    if (instance) {
      use(instance);
    }
  } catch (err) {
    console.error('Mixpanel error:', err);
  }
};

// Registered at module load, not in setup(): a 401 on page load signs the user
// out before anything has started Mixpanel.
addSignOutListener(() => withMixpanel((instance) => instance.reset()));

const compact = (properties: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(properties).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const trackEvent = (
  eventName: string,
  properties?: Record<string, unknown>
): void => {
  withMixpanel((instance) => {
    instance.track(eventName, properties);
  });
};

export const identifyMixpanelUser = (
  account: UserProfileResponse,
  activeProfile: EmployeeProfile | undefined,
  token: { facilityEmployeeId?: number; facilityId?: string }
): void =>
  withMixpanel((instance) => {
    const distinctId = account.id;
    if (!distinctId) {
      console.warn('Mixpanel: no account id, user left anonymous');
      return;
    }

    const currentDistinctId = instance.get_property('$user_id');
    if (currentDistinctId && distinctId !== currentDistinctId) {
      instance.reset();
    }

    instance.identify(distinctId);

    instance.register(
      compact({
        profile_id: activeProfile?.id,
        facility_employee_id: token.facilityEmployeeId,
        facility_id: token.facilityId,
        facility_name: activeProfile?.facilityName,
        company_name: activeProfile?.companyName,
        role: activeProfile?.role,
        department: activeProfile?.departmentLaborLevelDescription,
      })
    );

    instance.people.set(
      compact({
        $email: account.email,
        $first_name: activeProfile?.firstName,
        $last_name: activeProfile?.lastName,
        username: account.username,
        account_id: distinctId,
        profile_count: account.profiles?.length,
      })
    );
  });
